import {
  Download as DownloadIcon,
  Folder as FolderIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { FileItem as FileItemType } from "../../types/interfaces/filesStorage";
import { isTopDocumentFolderDownloadTarget } from "../../utils/topFolderDownload";
import { getFileIconForName } from "../preview/previewIcons";

export type TimeDisplayMode = "utc" | "local";

interface FileItemProps {
  item: FileItemType;
  onItemClick?: (item: FileItemType) => void;
  onDownloadClick?: (item: FileItemType) => void;
  onPreviewClick?: (item: FileItemType) => void;
  onMoreOptionsClick?: (item: FileItemType, event: React.MouseEvent) => void;
  showDownloadButton?: boolean;
  showPreviewButton?: boolean;
  showMoreOptions?: boolean;
  disableMoreOptions?: boolean;
  moreOptionsDisabledTooltip?: string;
  disabled?: boolean;
  isDownloading?: boolean;
  downloadProgress?: number;
  backgroundColor?: string;
  timeDisplayMode?: TimeDisplayMode;
  showFileDate?: boolean;
  hideEmptyLabel?: boolean;
  moreOptionsFoldersOnly?: boolean;
  disableFileClick?: boolean;
  topDocumentFolderRootPrefix?: string | null;
  mayDownloadTopDocumentFolder?: boolean;
  disableTopFolderZipDuringSync?: boolean;
  showAdminTopFolderZipCacheBadge?: boolean;
  adminTopFolderZipCacheReady?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const FileItem: React.FC<FileItemProps> = ({
  item,
  onItemClick,
  onDownloadClick,
  onPreviewClick,
  onMoreOptionsClick,
  showDownloadButton = true,
  showPreviewButton = false,
  showMoreOptions = false,
  disableMoreOptions = false,
  moreOptionsDisabledTooltip = "",
  disabled = false,
  isDownloading = false,
  downloadProgress,
  backgroundColor,
  timeDisplayMode = "local",
  showFileDate = true,
  hideEmptyLabel = false,
  moreOptionsFoldersOnly = false,
  disableFileClick = false,
  topDocumentFolderRootPrefix,
  mayDownloadTopDocumentFolder = false,
  disableTopFolderZipDuringSync = false,
  showAdminTopFolderZipCacheBadge = false,
  adminTopFolderZipCacheReady,
}) => {
  const progressValue = downloadProgress ?? 0;
  const downloadSpinnerIndeterminate =
    isDownloading && (downloadProgress === undefined || progressValue < 1);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isFolder = item.ftype === "folder";

  const itemClickIsActive =
    !disabled && !!onItemClick && !(disableFileClick && !isFolder);

  const handleItemClick = () => {
    if (!itemClickIsActive) return;
    onItemClick?.(item);
  };

  const handleDownloadClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled || isDownloading) return;
    const isTopDoc = isTopDocumentFolderDownloadTarget(
      item,
      topDocumentFolderRootPrefix,
    );
    if (
      disableTopFolderZipDuringSync &&
      item.ftype === "folder" &&
      isTopDoc
    ) {
      return;
    }

    if (onDownloadClick) {
      onDownloadClick(item);
    }
  };

  const handlePreviewClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled || !onPreviewClick) return;
    onPreviewClick(item);
  };

  const handleMoreOptionsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled || disableMoreOptions || !onMoreOptionsClick) return;
    onMoreOptionsClick(item, event);
  };

  const renderIcon = () => {
    if (isFolder) {
      return (
        <ListItemIcon>
          <FolderIcon sx={{ color: "primary.main" }} />
        </ListItemIcon>
      );
    }
    const FileTypeIcon = getFileIconForName(item.fname);
    return (
      <ListItemIcon>
        <FileTypeIcon sx={{ color: "primary.main" }} />
      </ListItemIcon>
    );
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const tz = timeDisplayMode === "utc" ? "UTC" : undefined;
    if (isMobile) {
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: tz,
      });
    }
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: tz,
    });
  };

  const renderText = () => {
    const folders = item.subfolder_count ?? 0;
    const files = item.direct_file_count ?? 0;
    const hasCounts = isFolder && (folders > 0 || files > 0);
    const isEmpty = isFolder && folders === 0 && files === 0;
    let countLabel: string | null = null;
    if (hasCounts) {
      if (folders > 0 && files > 0) {
        countLabel = `${folders} folder${folders !== 1 ? "s" : ""}, ${files} file${files !== 1 ? "s" : ""}`;
      } else if (folders > 0) {
        countLabel = `${folders} folder${folders !== 1 ? "s" : ""}`;
      } else {
        countLabel = `${files} file${files !== 1 ? "s" : ""}`;
      }
    } else if (isEmpty && !hideEmptyLabel) {
      countLabel = "empty";
    }
    const metadataSx = {
      whiteSpace: "nowrap" as const,
      ml: { xs: 1, sm: 2 },
      fontSize: { xs: "0.7rem", sm: "0.75rem" },
      flexShrink: 0,
      pr: { xs: 1, sm: 1.5 },
    };
    const uploading = item.is_uploading;
    const creating = item.is_creating;
    const pending = uploading || creating;
    return (
      <>
        <ListItemText
          primary={item.fname}
          primaryTypographyProps={{
            sx: { flex: 1, minWidth: 0 },
          }}
        />
        {pending && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {uploading ? "Uploading…" : "Creating…"}
          </Typography>
        )}
        {!pending && showFileDate && !isFolder && item.last_modified && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {formatDateTime(item.last_modified)}
          </Typography>
        )}
        {!pending && !isFolder && item.file_size != null && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {formatFileSize(item.file_size)}
          </Typography>
        )}
        {!pending && isFolder && countLabel && (
          <Typography variant="caption" color="text.secondary" sx={metadataSx}>
            {countLabel}
          </Typography>
        )}
      </>
    );
  };

  const renderActions = () => {
    const directFiles = item.direct_file_count ?? 0;
    const uploading = item.is_uploading;
    const creating = item.is_creating;
    const pending = uploading || creating;
    const shouldShowPreview =
      showPreviewButton && !pending && !isFolder && !!onPreviewClick;
    const isTopDocFolder = isTopDocumentFolderDownloadTarget(
      item,
      topDocumentFolderRootPrefix,
    );
    const topFolderRowBlockedBySync =
      !!disableTopFolderZipDuringSync && isTopDocFolder;
    const folderWantsDownload =
      !isFolder ||
      (isTopDocFolder
        ? mayDownloadTopDocumentFolder
        : directFiles > 0);
    const showTopFolderZipReadyCheck =
      showAdminTopFolderZipCacheBadge &&
      isFolder &&
      isTopDocFolder &&
      adminTopFolderZipCacheReady === true;
    const shouldShowDownload =
      showDownloadButton &&
      !pending &&
      (isFolder ? folderWantsDownload || isDownloading : true);
    const folderOkForMore = !moreOptionsFoldersOnly || item.ftype === "folder";
    const shouldShowMoreOptions =
      showMoreOptions && !!onMoreOptionsClick && !pending && folderOkForMore;

    if (
      !shouldShowDownload &&
      !shouldShowMoreOptions &&
      !shouldShowPreview &&
      !pending &&
      !showTopFolderZipReadyCheck
    ) {
      return null;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.25, sm: 0.5 } }}>
        {showTopFolderZipReadyCheck && (
          <Tooltip title="ZIP already built for current folder contents (reusable).">
            <CheckCircleIcon
              sx={{
                color: "success.main",
                fontSize: "1.35rem",
                flexShrink: 0,
              }}
              aria-hidden
            />
          </Tooltip>
        )}
        {pending && showMoreOptions && (
          <IconButton disabled sx={{ cursor: "default" }}>
            <CircularProgress size={24} />
          </IconButton>
        )}
        {pending && !showMoreOptions && (
          <CircularProgress size={16} sx={{ ml: { xs: 0.5, sm: 1 } }} />
        )}
        {shouldShowPreview && (
          <Tooltip title="Preview">
            <IconButton
              onClick={handlePreviewClick}
              disabled={disabled}
              className="text-gray-500 hover:text-gray-700"
              aria-label={`Preview ${item.fname}`}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        )}
        {shouldShowDownload && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <IconButton
              onClick={handleDownloadClick}
              disabled={disabled || isDownloading || topFolderRowBlockedBySync}
              className="text-gray-500 hover:text-gray-700"
              aria-label={
                topFolderRowBlockedBySync
                  ? `${item.fname} — folder sync in progress`
                  : isDownloading
                    ? `${item.fname} — ZIP building`
                    : `Download ${item.fname}`
              }
            >
              {isDownloading ? (
                <CircularProgress
                  size={24}
                  variant={
                    downloadSpinnerIndeterminate
                      ? "indeterminate"
                      : "determinate"
                  }
                  {...(downloadSpinnerIndeterminate
                    ? {}
                    : { value: progressValue })}
                  sx={{
                    color:
                      progressValue >= 100
                        ? "success.main"
                        : "primary.main",
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  }}
                />
              ) : (
                <DownloadIcon />
              )}
            </IconButton>
          </Box>
        )}
        {shouldShowMoreOptions &&
          (disableMoreOptions && moreOptionsDisabledTooltip ? (
            <Tooltip title={moreOptionsDisabledTooltip}>
              <span>
                <IconButton
                  onClick={handleMoreOptionsClick}
                  disabled={true}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <MoreVertIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <IconButton
              onClick={handleMoreOptionsClick}
              disabled={disabled || disableMoreOptions}
              className="text-gray-500 hover:text-gray-700"
            >
              <MoreVertIcon />
            </IconButton>
          ))}
      </Box>
    );
  };

  const ListItemAny = ListItem as unknown as React.ComponentType<
    Record<string, unknown>
  >;

  return (
    <ListItemAny
      {...(itemClickIsActive ? { button: true, onClick: handleItemClick } : {})}
      disableGutters
      className="w-full"
      style={{
        padding: isMobile ? "10px 12px" : "4px 16px",
        backgroundColor: backgroundColor || "transparent",
        cursor: itemClickIsActive ? undefined : "default",
      }}
      sx={{
        alignItems: "center",
        py: isMobile ? 0.5 : undefined,
        "& .MuiListItemText-root": { minWidth: 0, flex: "1 1 auto" },
        "& .MuiListItemText-primary": isMobile
          ? {
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              whiteSpace: "normal",
              wordBreak: "break-word",
              lineHeight: 1.35,
            }
          : {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
      }}
    >
      {renderIcon()}
      {renderText()}
      {renderActions()}
    </ListItemAny>
  );
};

export default FileItem;
